import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2, Users } from 'lucide-react'
import { CONSENT_LABELS, SOURCE_LABELS, audienceCount } from '../../../services/crmService'

const SOURCES = Object.keys(SOURCE_LABELS)
const CONSENT_LEVELS = ['opted_in', 'implied']

function toggle(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

// The live recipient count comes from the same crm_resolve_audience the send
// function uses, so the number here is the number that gets mailed — not an
// estimate that drifts from reality between preview and send.
export default function AudiencePicker({ audience, onChange, disabled = false }) {
  const [count, setCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const sources = audience.sources ?? []
  const consent = audience.consent ?? []
  const includeNonConsented = !!audience.includeNonConsented

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    audienceCount(audience)
      .then((value) => { if (!cancelled) setCount(value) })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not count the audience') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [audience])

  const update = (patch) => onChange({ ...audience, ...patch })

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" />
          Audience
        </span>
        <span className="text-right">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!loading && error && <span className="text-xs text-destructive">{error}</span>}
          {!loading && !error && (
            <>
              <span className="text-2xl font-semibold">{count?.toLocaleString() ?? '—'}</span>
              <span className="ml-1.5 text-sm text-muted-foreground">recipients</span>
            </>
          )}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <fieldset>
          <legend className="mb-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            Sources <span className="normal-case">(any, blank = all)</span>
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {SOURCES.map((value) => (
              <label
                key={value}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${
                  sources.includes(value)
                    ? 'border-primary bg-primary/10 font-medium'
                    : 'border-border text-muted-foreground'
                } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={sources.includes(value)}
                  disabled={disabled}
                  onChange={() => update({ sources: toggle(sources, value) })}
                />
                {SOURCE_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            Consent level
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {CONSENT_LEVELS.map((value) => (
              <label
                key={value}
                className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${
                  consent.includes(value)
                    ? 'border-primary bg-primary/10 font-medium'
                    : 'border-border text-muted-foreground'
                } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={consent.includes(value)}
                  disabled={disabled}
                  onChange={() => update({ consent: toggle(consent, value) })}
                />
                {CONSENT_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <label
        className={`flex gap-3 rounded-md border p-3 text-sm ${
          includeNonConsented
            ? 'border-[var(--orange-6)] bg-[var(--orange-3)]'
            : 'border-border'
        } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
      >
        <input
          type="checkbox"
          checked={includeNonConsented}
          disabled={disabled}
          onChange={(e) => update({ includeNonConsented: e.target.checked })}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Also mail buyers who never opted in</span>
          <span className={`block text-xs ${includeNonConsented ? 'text-[var(--orange-11)]' : 'text-muted-foreground'}`}>
            {includeNonConsented ? (
              <span className="inline-flex gap-1.5">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Spam complaints from these recipients hurt deliverability for
                every email from getdub.com, including password resets. Use it
                deliberately, not by default.
              </span>
            ) : (
              'Shopify customers who bought something but never checked the marketing box.'
            )}
          </span>
        </span>
      </label>
    </div>
  )
}
